import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Copy, Plus, ShieldOff } from 'lucide-react';
import { ApiScope, type ApiScope as ApiScopeCode } from '@clawhire/shared';
import { api } from '@/lib/api';
import { EMPTY, formatDate, formatDateTime } from '@/lib/format';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { DateInput } from '@/components/ui/date-input';
import { SkeletonLines } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from '@/components/ui/toaster';
import { AdminOnly, SettingsSection } from '@/pages/settings/SettingsSection';
import { unwrapList } from '@/pages/settings/useSettings';

export interface ApiTokenRow {
  id: string;
  name: string;
  tokenPrefix: string;
  scopes: ApiScopeCode[] | string[];
  lastUsedAt: string | null;
  revoked: boolean;
  revokedAt: string | null;
  expiresAt: string | null;
  owner?: { name: string; email: string } | null;
  createdAt: string;
  createdBy?: string | null;
}

export interface ApiTokenCreated {
  id: string;
  name: string;
  scopes: string[];
  tokenPrefix: string;
  /** Plaintext, returned exactly once. */
  token: string;
}

const TOKENS_KEY = ['api-tokens'] as const;

const SCOPE_HELP: Record<ApiScopeCode, string> = {
  READ: 'read positions, candidates, submissions, partners',
  WRITE: 'create and change records',
  DOCUMENTS: 'generate JDs and profiles',
  IMPORT: 'import records from the legacy workspace',
  ADMIN: 'settings, users and tokens',
};

/** Personal access tokens for agents and scripts (spec §4.5, §10). */
export function ApiTokensSettings() {
  return (
    <SettingsSection
      title="API tokens"
      description="Bearer tokens for agents and scripts (dcpat_…). Scopes limit what a token may do; revoke a token to stop it immediately."
    >
      <AdminOnly what="API tokens">
        <TokensAdmin />
      </AdminOnly>
    </SettingsSection>
  );
}

function tokenStatus(t: ApiTokenRow): { label: string; className: string } {
  if (t.revoked) return { label: 'Revoked', className: 'bg-muted text-muted-foreground' };
  if (t.expiresAt && new Date(t.expiresAt).getTime() < Date.now())
    return {
      label: 'Expired',
      className: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
    };
  return {
    label: 'Active',
    className: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
  };
}

function TokensAdmin() {
  const queryClient = useQueryClient();
  const tokens = useQuery({
    queryKey: TOKENS_KEY,
    queryFn: async () => unwrapList<ApiTokenRow>(await api.get<unknown>('/api-tokens')),
  });
  const [createOpen, setCreateOpen] = React.useState(false);

  const revoke = useMutation({
    mutationFn: (id: string) => api.delete(`/api-tokens/${id}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: TOKENS_KEY });
      toast.success('Token revoked');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => setCreateOpen(true)}>
          <Plus aria-hidden="true" /> New token
        </Button>
      </div>

      {tokens.isLoading ? (
        <SkeletonLines lines={4} />
      ) : tokens.isError ? (
        <p role="alert" className="text-sm text-destructive">
          Tokens could not be loaded: {tokens.error.message}
        </p>
      ) : tokens.data && tokens.data.length === 0 ? (
        <EmptyState
          compact
          title="No API tokens"
          description="Create one for Claude, the MCP connector or a script."
        />
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <Table>
            <caption className="sr-only">API tokens</caption>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>Name</TableHead>
                <TableHead>Prefix</TableHead>
                <TableHead>Scopes</TableHead>
                <TableHead>Last used</TableHead>
                <TableHead>Expires</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">
                  <span className="sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(tokens.data ?? []).map((t) => {
                const status = tokenStatus(t);
                return (
                  <TableRow key={t.id} className={t.revoked ? 'text-muted-foreground' : undefined}>
                    <TableCell className="font-medium">
                      {t.name}
                      <div className="text-xs font-normal text-muted-foreground">
                        {[t.owner?.name ?? t.createdBy, formatDate(t.createdAt)].filter(Boolean).join(' · ')}
                      </div>
                    </TableCell>
                    <TableCell>
                      <code className="rounded bg-muted px-1.5 py-0.5 text-xs">{t.tokenPrefix}…</code>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {t.scopes.map((s) => (
                          <Badge key={s} variant="secondary" className="font-normal">
                            {ApiScope.is(s) ? ApiScope.label(s) : s}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="whitespace-nowrap tabular-nums">
                      {t.lastUsedAt ? (
                        formatDateTime(t.lastUsedAt)
                      ) : (
                        <span className="text-muted-foreground">never</span>
                      )}
                    </TableCell>
                    <TableCell className="whitespace-nowrap tabular-nums">
                      {t.expiresAt ? (
                        formatDate(t.expiresAt)
                      ) : (
                        <span className="text-muted-foreground">{EMPTY}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge className={`border-transparent shadow-none ${status.className}`}>
                        {status.label}
                      </Badge>
                      {t.revokedAt ? (
                        <div className="text-xs text-muted-foreground">{formatDate(t.revokedAt)}</div>
                      ) : null}
                    </TableCell>
                    <TableCell className="text-right">
                      {t.revoked ? null : (
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={revoke.isPending}
                          onClick={() => {
                            if (
                              window.confirm(
                                `Revoke the token "${t.name}"? Anything using it stops working immediately.`,
                              )
                            )
                              revoke.mutate(t.id);
                          }}
                        >
                          <ShieldOff aria-hidden="true" /> Revoke
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <CreateTokenDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={() => queryClient.invalidateQueries({ queryKey: TOKENS_KEY })}
      />
    </div>
  );
}

function CreateTokenDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: () => unknown;
}) {
  const [name, setName] = React.useState('');
  const [scopes, setScopes] = React.useState<ApiScopeCode[]>(['READ']);
  const [expires, setExpires] = React.useState<string | null>(null);
  const [created, setCreated] = React.useState<ApiTokenCreated | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (open) {
      setName('');
      setScopes(['READ']);
      setExpires(null);
      setCreated(null);
      setError(null);
    }
  }, [open]);

  const create = useMutation({
    mutationFn: () =>
      api.post<ApiTokenCreated>('/api-tokens', {
        name: name.trim(),
        scopes,
        expiresAt: expires ? new Date(`${expires}T23:59:59Z`).toISOString() : null,
      }),
    onSuccess: async (res) => {
      setCreated(res);
      await onCreated();
    },
    onError: (err: Error) => setError(err.message),
  });

  const toggle = (scope: ApiScopeCode, on: boolean) =>
    setScopes((prev) => (on ? Array.from(new Set([...prev, scope])) : prev.filter((s) => s !== scope)));

  const copy = async () => {
    if (!created) return;
    try {
      await navigator.clipboard.writeText(created.token);
      toast.success('Token copied');
    } catch {
      toast.error('Could not copy; select the token and copy it manually');
    }
  };

  const canSubmit = name.trim().length > 0 && scopes.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        {created ? (
          <div className="space-y-4">
            <DialogHeader>
              <DialogTitle>Token created</DialogTitle>
              <DialogDescription>
                {created.name} ·{' '}
                {created.scopes.map((s) => (ApiScope.is(s) ? ApiScope.label(s) : s)).join(', ')}
              </DialogDescription>
            </DialogHeader>
            <p
              role="alert"
              className="flex items-start gap-2 rounded-md border border-amber-300/60 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200"
            >
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              <span>
                Copy the token now. It is shown only once and cannot be retrieved later; create a new one if
                you lose it.
              </span>
            </p>
            <div className="space-y-2">
              <Label htmlFor="new-api-token">New API token</Label>
              <div className="flex gap-2">
                <Input
                  id="new-api-token"
                  readOnly
                  value={created.token}
                  className="font-mono text-xs"
                  onFocus={(e) => e.currentTarget.select()}
                />
                <Button type="button" variant="outline" onClick={() => void copy()} aria-label="Copy token">
                  <Copy aria-hidden="true" /> Copy
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Use it as <code>Authorization: Bearer {created.tokenPrefix}…</code>
              </p>
            </div>
            <DialogFooter>
              <Button type="button" onClick={() => onOpenChange(false)}>
                Done
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              setError(null);
              if (canSubmit) create.mutate();
            }}
          >
            <DialogHeader>
              <DialogTitle>New API token</DialogTitle>
              <DialogDescription>
                Give it a name you will recognise in the audit log and pick the scopes it needs.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <Label htmlFor="api-token-name">Name</Label>
              <Input
                id="api-token-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Claude connector"
                maxLength={80}
                autoFocus
                required
              />
            </div>
            <fieldset className="space-y-2">
              <legend className="text-sm font-medium">Scopes</legend>
              {ApiScope.codes.map((scope) => {
                const id = `api-token-scope-${scope}`;
                return (
                  <div key={scope} className="flex items-start gap-2">
                    <Checkbox
                      id={id}
                      checked={scopes.includes(scope)}
                      onCheckedChange={(v) => toggle(scope, v === true)}
                      className="mt-0.5"
                    />
                    <Label htmlFor={id} className="cursor-pointer font-normal leading-snug">
                      <span className="font-medium">{ApiScope.label(scope)}</span>
                      <span className="text-muted-foreground"> – {SCOPE_HELP[scope]}</span>
                    </Label>
                  </div>
                );
              })}
              {scopes.length === 0 ? (
                <p className="text-xs text-destructive">Pick at least one scope.</p>
              ) : null}
            </fieldset>
            <div className="space-y-2">
              <Label htmlFor="api-token-expires">Expires on (optional)</Label>
              <DateInput id="api-token-expires" value={expires} onChange={setExpires} />
            </div>
            {error ? (
              <p role="alert" className="text-sm text-destructive">
                {error}
              </p>
            ) : null}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" loading={create.isPending} disabled={!canSubmit}>
                Create token
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
